import React, { useEffect, useRef, useState } from 'react'
import { sharePermissionConstants } from '../../constants/sharePermissionConstants'
import {
  LinkifiedText,
  renderFieldLabel,
  renderPermissionBoxHeader,
  renderRequestStatus,
  renderSpinner,
  renderStatusMsg,
  validateTextareaContent,
} from './helpers'
import TextBox from '../TextBox'
import { apiErrorMessages } from './apiErrorMessages'
import {
  handleSharePermissionRequestActions,
  ACTION_TEXT,
  ENDPOINT,
} from './apiUtils'

const RenderRequestDetails = (props) => {
  const { statusMsg, user } = props
  const textArea = useRef(null)
  const [requestDetails, setRequestDetails] = useState(
    props?.requestDetails || {},
  )
  const [comment, setComment] = useState('')
  const [commentErrorMsg, setCommentErrorMsg] = useState('')
  const [statusMessage, setStatusMessage] = useState(
    statusMsg || {
      msg: '',
      status: 'success',
    },
  )
  const [
    isRequestDetailsActionInProgress,
    setRequestDetailsActionInProgress,
  ] = useState(false)
  const [
    isFetchRequestDetailsForIdFromParent,
    setIsfetchrequestDetailsForIdFromParent,
  ] = useState(false)

  const updateStatusMessage = (msg, status) => {
    setStatusMessage({ msg, status })
  }

  const handleError = (error) => {
    // Graceful error message with correct progressive tense (approving/rejecting)
    const { prog } = ACTION_TEXT[error?.action] || {}
    const fallback = sharePermissionConstants.statusMsg.requestActionError?.replace(
      '{operation}',
      prog,
    )

    updateStatusMessage(error?.message || fallback, error?.status || 'error')
    console.error(error) // keep optional logging
  }

  const handleActionResponse = (response, action) => {
    // handle success path
    if (response?.status === 'success') {
      if (action === 'get') {
        setRequestDetails(response?.data || {})
        updateStatusMessage('', 'success')
        return
      }

      // eslint-disable-next-line no-use-before-define
      getRequestDetails(response?.message || '')
    }

    // Non-success: show error uniformly
    updateStatusMessage(response.msg, response.status ?? 'error')
  }

  const fetchSharePermissionRequest = async (successMsg, requestId) => {
    setRequestDetailsActionInProgress(true)
    try {
      const id = String(requestId ?? '').trim()

      if (!id) {
        updateStatusMessage(apiErrorMessages.GENERAL.missingRequestId, 'error')
        return null
      }

      if (!sharePermissionConstants.RequestIdFormat.test(id)) {
        updateStatusMessage(apiErrorMessages.GENERAL.invalidRequestId, 'error')
        return null
      }

      const response = await handleSharePermissionRequestActions(id, {}, 'get')
      const { status, data, msg } = response ?? {}

      if (status === 'success') {
        const message =
          successMsg ===
            sharePermissionConstants.statusMsg.requestActionResumed &&
          response?.data?.status === 'REJECTED'
            ? sharePermissionConstants.statusMsg.requestActionResumeRejected
            : successMsg
        const responseStatus =
          sharePermissionConstants.statusMsg.requestActionResumed &&
          response?.data?.status === 'REJECTED'
            ? 'error'
            : response?.status
        setRequestDetails(data ?? {})
        if (message) updateStatusMessage(message, responseStatus)
        return data ?? {}
      }

      // Non-success or unexpected shape
      updateStatusMessage(
        msg ?? apiErrorMessages.GENERAL.somethingWentWrong,
        status ?? 'error',
      )
      return null
    } catch (error) {
      handleError(error)
      return null
    } finally {
      setIsfetchrequestDetailsForIdFromParent(false)
      setRequestDetailsActionInProgress(false)
    }
  }

  const getRequestDetails = (successMsg = '') => {
    const timer = setTimeout(() => {
      fetchSharePermissionRequest(successMsg, requestDetails?.request_id)
    }, 100)
    return () => clearTimeout(timer)
  }

  useEffect(() => {
    if (props?.requestDetailsForId) {
      setIsfetchrequestDetailsForIdFromParent(true)
      fetchSharePermissionRequest('', props?.requestDetailsForId || '')
    }
  }, [props?.requestDetailsForId])

  /* Unified Share Permission Request handler */
  const constructParams = (action) => {
    const trimmedComment = typeof comment === 'string' ? comment.trim() : ''
    return action === 'withdraw'
      ? {}
      : {
          action,
          ...(trimmedComment && { comment: trimmedComment }),
        }
  }

  const handleUnifiedSharePermissionRequest = async (action) => {
    //validate against ACTION_TEXT (kept behavior)
    if (action !== 'get' && !ACTION_TEXT?.[action]) {
      updateStatusMessage(
        apiErrorMessages.GENERAL.unsupportedAction?.replace(
          '{action}',
          String(action),
        ),
        'error',
      )
      return
    }

    try {
      setRequestDetailsActionInProgress(true)

      const requestId = requestDetails?.request_id
      if (!requestId) {
        updateStatusMessage(apiErrorMessages.GENERAL.missingRequestId, 'error')
        return
      }

      const endPoint = action !== 'get' ? ENDPOINT[action] : ''
      const path = `${requestId}${endPoint}`
      const params = constructParams(action)

      const response = await handleSharePermissionRequestActions(
        path,
        params,
        action,
      )
      handleActionResponse(response, action)
    } catch (error) {
      handleError(error)
    } finally {
      setRequestDetailsActionInProgress(false)
    }
  }

  const buttonActions = (button) => {
    if (button === 'close') {
      return props.onClose(false)
    }
    if (button === 'update') {
      return props.updateActionHandler(requestDetails)
    }
    return handleUnifiedSharePermissionRequest(button)
  }

  const checkButtonVisibility = (button) => {
    const trimmedComment = typeof comment === 'string' ? comment.trim() : ''
    const noError = commentErrorMsg === ''

    // Visibility rules per button → return true to SHOW
    const canShow = {
      close: () => true,
      approve: () => noError,
      reject: () => noError && trimmedComment !== '',
      withdraw: () => noError,
      resume: () => noError,
      update: () => noError,
    }[button]

    // Unknown buttons are hidden by default
    return !(canShow ? canShow() : false)
  }

  const handleCommentChange = (evt) => {
    const { value } = evt.target
    const trimmedComment = value.trim()

    // Always update the state with the raw value
    setComment(value)

    // Reset messages when user is typing
    setCommentErrorMsg('')
    updateStatusMessage('', 'success')

    // Only validate on blur, and only if there's non-empty content
    if (evt.type === 'blur' && trimmedComment) {
      const errorMsg = validateTextareaContent(trimmedComment, 'comment')
      if (errorMsg) {
        setCommentErrorMsg(errorMsg)
      }
    }
  }

  const getVisibleActions = () => {
    const { requestor = '', approver = '', status = '' } = requestDetails || {}
    const allActions = sharePermissionConstants.requestedDetailsActions

    // Who is the viewer?
    const isRequestor = requestor === user.username || requestor === user.email
    const isApprover = approver === user.username || approver === user.email

    // Decide which action keys are allowed for the given status
    const allowedKeysByStatus = (requestStatus) => {
      switch (requestStatus) {
        case 'PENDING_APPROVAL':
          return new Set(['update', 'withdraw', 'approve', 'reject', 'close'])
        case 'PENDING_INFOSEC_APPROVAL':
          return new Set(['resume', 'close'])
        case 'APPROVED':
        case 'REJECTED':
        case 'CANCELLED':
          return new Set(['close'])
        default:
          return new Set(allActions.map((action) => action.key))
      }
    }

    const allowedKeys = allowedKeysByStatus(status)

    /* Visibility check:
         - If user is requestor and/or approver, allow actions visible to either role (union).
         - If user is neither, don't restrict by role.
        */
    const canSee = (action) => {
      if (!isRequestor && !isApprover) return true
      const visibleFor = action.visibleFor || []
      return (
        (isRequestor && visibleFor.includes('requestor')) ||
        (isApprover && visibleFor.includes('approver'))
      )
    }

    // Final list
    return allActions
      .filter((action) => allowedKeys.has(action.key))
      .filter(canSee)
      .filter((action) => action.isVisible)
      .filter((action) =>
        Object.keys(requestDetails).length === 0
          ? action.key === 'close'
          : true,
      )
  }

  const renderCommentBox = () => {
    const baseComment = sharePermissionConstants.fields.comment
    const commentConstant = {
      ...baseComment,
      ...(props?.isApproveRejectRequest === 'approve' && {
        isMandatory: false,
      }),
    }

    return (
      <div className="share__permission-comment">
        <div className="share__permission-comment-content">
          {renderFieldLabel(
            commentConstant.title,
            '',
            commentConstant.isMandatory,
          )}
          <div className="share__permission-comment-content_container">
            <TextBox
              ref={textArea}
              name={commentConstant.key}
              id={commentConstant.key}
              className={commentConstant.className}
              placeholder={commentConstant.placeholder}
              maxCharacters={commentConstant.maxCharacters}
              value={comment}
              onChangeText={(evt) => handleCommentChange(evt)}
              onBlurText={(evt) => handleCommentChange(evt)}
              errorText={commentErrorMsg}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderRequestedDetailsAction = () => {
    const actions = getVisibleActions()

    return (
      <div className="share__permission_action-container">
        {actions.map((action, index) => {
          return (
            <div className="share__permission_action" key={index}>
              <button
                className={`${action.className} ${
                  checkButtonVisibility(action.key) ? 'disable__button' : ''
                }`}
                disabled={checkButtonVisibility(action.key)}
                onClick={() => buttonActions(action.key)}
              >
                <span>{action.title}</span>
              </button>
            </div>
          )
        })}
      </div>
    )
  }

  const renderRitmLink = () => {
    const { deployEnv } = props
    const { RitmLink, requestDetailsLabels } = sharePermissionConstants
    const href = deployEnv.isProd
      ? RitmLink.prod.replace('{ritmID}', requestDetails?.infosec_ticket)
      : RitmLink.qa.replace('{ritmID}', requestDetails?.infosec_ticket)
    return (
      <div className="share__permission-request_details">
        <div className="share__permission-request_details_label">
          {requestDetailsLabels.infosec_ticket}
        </div>
        <div className="share__permission-request_details_value">
          <a href={href} target="_blank" rel="noopener noreferrer">
            {requestDetails?.infosec_ticket}
          </a>
        </div>
      </div>
    )
  }

  const renderStatus = () => {
    return (
      <div className="share__permission-request_details">
        <div className="share__permission-request_details_label">
          {sharePermissionConstants.requestDetailsLabels.status}
        </div>
        {renderRequestStatus(requestDetails?.status || '')}
      </div>
    )
  }

  const renderContentWithLink = (renderFor) => {
    return (
      <div className="share__permission-request_details">
        <div className="share__permission-request_details_label">
          {sharePermissionConstants.requestDetailsLabels[renderFor]}
        </div>
        <div className="share__permission-request_details_value">
          {' '}
          <LinkifiedText text={requestDetails?.[renderFor] || ''} />{' '}
        </div>
      </div>
    )
  }

  const renderRequestData = (renderFor, type = '') => {
    let data = requestDetails?.[renderFor] || ''
    data = Array.isArray(data) ? data.join(', ') : data
    data =
      type === 'date'
        ? new Date(data).toDateString() +
          ' ' +
          new Date(data).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC',
          })
        : data
    data =
      renderFor === 'duration_in_days'
        ? `${data} ${
            data > 1
              ? sharePermissionConstants.requestDetailsLabels.daysText
              : sharePermissionConstants.requestDetailsLabels.dayText
          }`
        : data
    return (
      <div className="share__permission-request_details">
        <div className="share__permission-request_details_label">
          {sharePermissionConstants.requestDetailsLabels[renderFor]}
        </div>
        <div className="share__permission-request_details_value">{data}</div>
      </div>
    )
  }

  const renderRequestedDetails = () => {
    return (
      <div className="share__permission-request_details_container">
        <div className="share__permission-request_details_content">
          <div className="share__permission-request_details-group share__permission_request_group-id_status">
            {renderRequestData('request_id')}
            {renderStatus()}
          </div>
          <div className="share__permission-request_details-group">
            {renderRequestData('approver')}
            {requestDetails?.infosec_ticket && renderRitmLink()}
            {requestDetails?.status === 'APPROVED' &&
              renderRequestData('expire_on', 'date')}
          </div>
          {renderRequestData('sharee_email_addresses')}
          {renderContentWithLink('business_justification')}
          {renderContentWithLink('data_source')}
          <div className="share__permission-request_details-group">
            {renderRequestData('data_classification')}
            {renderRequestData('infosec_domain')}
            {renderRequestData('duration_in_days')}
          </div>
          <div className="share__permission-request_details-group">
            {requestDetails?.approver_action_at &&
              renderRequestData('approver_action_at')}
            {requestDetails?.approver_comment &&
              renderRequestData('approver_comment')}
          </div>
          <div className="share__permission-request_details-group">
            {requestDetails?.infosec_action_at &&
              renderRequestData('infosec_action_at', 'date')}
            {requestDetails?.created_at &&
              renderRequestData('created_at', 'date')}
            {requestDetails?.last_updated_at &&
              renderRequestData('last_updated_at', 'date')}
            {requestDetails?.status === 'PENDING_APPROVAL' &&
              (requestDetails?.approver === user.username ||
                requestDetails?.approver === user.email) &&
              (requestDetails?.requestor !== user.username ||
                requestDetails?.requestor !== user.email) &&
              renderCommentBox()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="ppvx_modal-backdrop share__permission_request_popup"
      id="request_permission_details_render_form"
      role="dialog"
      aria-hidden={props.showPermissionBox ? 'false' : 'true'}
      data-ppvx_modal-background-close="true"
    >
      <article className="ppvx_modal" tabIndex="-1">
        {renderPermissionBoxHeader(
          sharePermissionConstants.requestDetailsLabels.title,
          props.onClose,
        )}
        <section className="share__permission-section">
          {(statusMessage?.msg || '') !== '' && renderStatusMsg(statusMessage)}
          {Object.keys(requestDetails).length > 0 &&
            !isFetchRequestDetailsForIdFromParent &&
            renderRequestedDetails()}
        </section>
        {renderRequestedDetailsAction()}
        {isRequestDetailsActionInProgress && renderSpinner()}
      </article>
    </div>
  )
}
export default RenderRequestDetails
